// LoginPage — chỉ wire @cnv/ui/pages/auth/LoginPage với branding + Google button SaaS-specific.
//
// Page logic + form sống ở @cnv/ui per HOST_CONTRACT.md (1 cty = 1 component lib).
// Host's `@host/auth.useAuth()` (shim AuthContext.jsx) cung cấp:
//   - login(email, pwd) → authApi.login + setSession
//   - loginWithGoogle(idToken) → authApi.loginWithGoogle + setSession
//   - listMyTenants() — multi-tenant routing /select-workspace vs /chat
//
// Google sign-in của SaaS dùng `window.google.accounts.id.renderButton` (ID token flow)
// → host responsibility, mount qua googleSlot.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Smartphone } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import LoginPage from '@cnv/ui/pages/auth/LoginPage';
import { useAuth } from '../contexts/AuthContext';
import { homeRouteFor } from '../lib/homeRoute';
import toast from 'react-hot-toast';
import '../i18n-host';

// Nút Google chỉ hiện khi có client id — build thiếu `.env.production` sẽ tự ẩn.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_WEB_CLIENT_ID || '';

// msg`` = descriptor (chưa dịch) — hằng module scope không gọi hook được. LoginPage của
// cnv-ui nhận cả descriptor lẫn chuỗi thường trong errorMap (xem translate() bên đó).
const SAAS_ERROR_MAP = {
  account_not_registered: msg`Email Google chưa đăng ký — vui lòng đăng ký tài khoản trước`,
  google_id_token_invalid: msg`Google token không hợp lệ, thử lại`,
  google_no_email: msg`Tài khoản Google không có email`,
  google_oauth_not_configured: msg`Server chưa cài đặt Google OAuth, báo admin`,
  no_accessible_tenant: msg`Không có workspace khả dụng — liên hệ admin`,
};

// GSI auth-code flow — GỘP login + Google Calendar trong 1 CONSENT (giống Core connect).
// initCodeClient(popup) trả `code` → POST /auth/google → BE exchange (client secret từ file
// JSON, redirect_uri='postmessage') → login + lưu refresh token calendar vào
// metadata.google_calendar (server-side, bền vững). KHÔNG còn bước "Kết nối Calendar" lần 2.
const GOOGLE_SCOPE = 'openid email profile https://www.googleapis.com/auth/calendar.events';

function GoogleButton() {
  const { loginWithGoogle } = useAuth();
  const { t, i18n } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const codeClientRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    let tries = 0;
    const init = () => {
      if (cancelled) return;
      const oauth2 = window.google?.accounts?.oauth2;
      if (!oauth2?.initCodeClient) {
        if (tries++ < 40) setTimeout(init, 100);
        return;
      }
      try {
        codeClientRef.current = oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPE,
          ux_mode: 'popup',
          callback: async (resp) => {
            if (!resp?.code) { toast.error(t`Không nhận được mã Google`); setSubmitting(false); return; }
            try {
              const user = await loginWithGoogle({ code: resp.code });
              const who = user?.display_name || user?.email || '';
              toast.success(t`Xin chào ${who}`);
              window.dispatchEvent(new Event('cnv:auth-changed'));
            } catch (err) {
              const code = err?.response?.data?.error?.code;
              toast.error(SAAS_ERROR_MAP[code] ? i18n._(SAAS_ERROR_MAP[code]) : t`Đăng nhập Google thất bại — thử lại`);
            } finally {
              setSubmitting(false);
            }
          },
          error_callback: () => setSubmitting(false),
        });
      } catch (e) {
        console.warn('GSI code client init failed:', e?.message);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [loginWithGoogle]);

  const startGoogle = () => {
    if (!codeClientRef.current) { toast.error(t`Google chưa sẵn sàng, thử lại`); return; }
    setSubmitting(true);
    codeClientRef.current.requestCode();
  };

  return (
    <button
      type="button"
      onClick={startGoogle}
      disabled={submitting}
      className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-60 transition"
    >
      {submitting ? (
        <>
          <Loader2 size={16} className="animate-spin text-slate-500" />
          <span className="text-sm font-medium text-slate-700">{t`Đang đăng nhập với Google...`}</span>
        </>
      ) : (
        <>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.13 4.13 0 0 1-1.8 2.71v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A8.99 8.99 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3-2.33z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
          </svg>
          <span className="text-sm font-medium text-slate-700">{t`Tiếp tục với Google`}</span>
        </>
      )}
    </button>
  );
}

// Nút dẫn sang màn OTP — đường đăng nhập CHÍNH của Connect (sếp Phú 01/09: *"lấy OTP làm
// đăng nhập chính, sau đó Google và tới mật khẩu"*). Vì là đường chính nên nó mang dáng nút
// chính (nền xanh đặc), khác Google và mật khẩu là hai đường phụ dáng viền.
//
// Đặt ở khe `otpSlot` của kernel — khe mở riêng cho đường đứng đầu, Core và CNV Work SaaS
// không truyền nên hai hệ đó không thấy gì.
function OtpButton() {
  const { t } = useLingui();
  return (
    <Link to="/login/otp"
      title={t`Đăng nhập bằng mã gửi qua tin nhắn`}
      className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition">
      <Smartphone size={18} className="text-white" />
      <span className="text-sm font-semibold text-white"><Trans>Đăng nhập bằng mã OTP</Trans></span>
    </Link>
  );
}

export default function LoginPageSaas() {
  const { t } = useLingui();
  // SaaS routing sau đăng nhập:
  //   - Nhiều workspace → /select-workspace để người dùng chọn.
  //   - 1 workspace: chỉ vào /chat khi tenant đã bật module team_chat; ngược lại
  //     /dashboard (an toàn cho mọi tenant). Trước đây ép /chat → tenant tắt
  //     team_chat gặp lỗi "Module team_chat chưa enable" (TC-292 / ticket 46d0d32b).
  const redirectTo = (_user, tenants) => {
    if (Array.isArray(tenants) && tenants.length > 1) return '/select-workspace';
    const onlyTenant = Array.isArray(tenants) && tenants.length === 1 ? tenants[0] : null;
    // Đích + nhánh dự phòng khai MỘT CHỖ: lib/homeRoute.js. Đừng viết lại ternary ở đây —
    // trước 27/08/2026 mỗi cửa vào tự ghi đích riêng và có cửa kẹt ở module đã gỡ.
    return homeRouteFor(onlyTenant);
  };

  return (
    <LoginPage
      logoSrc="/siteconnect.png"
      logoAlt="Site Connect"
      brandName="Site Connect"
      tagline="PROJECTS · SCHEDULING · TIME TRACKING"
      // subtitle/signupText/signupCtaText: KHÔNG truyền — dùng default đã dịch của
      // @cnv/ui LoginPage ("Chào mừng trở lại" / "Chưa có tài khoản?" / "Đăng ký miễn phí").
      subtitleHint={t`Đăng nhập để tiếp tục quản lý công việc`}
      // `blue` — CNV Connect brand xanh (#0099DB, xem cnv-colors.json trong
      // cnvconnect-mobile/docs/CNV-Connect-AppStore). `pink` là màu CNV Work.
      variant="blue"
      // Thứ tự Connect: OTP (chính) → Google → gạch "hoặc" → form mật khẩu.
      otpSlot={<OtpButton />}
      googleSlot={GOOGLE_CLIENT_ID ? <GoogleButton /> : null}
      signupHref="/signup"
      // msgid phải là TIẾNG VIỆT (sourceLocale='vi'). Trước 2026-08-13 chỗ này đặt thẳng
      // chuỗi tiếng Anh làm msgid mà không extract lại catalog `host` → không locale nào có
      // key đó, bản production (babel strip message) render RA HASH "NiYb3k" ngay dưới card
      // login. Đổi chữ ở đây thì BẮT BUỘC `lingui extract` + dịch + `lingui compile`.
      // 14/10/2026 (sếp Phú: «chỉ ghi đúng thứ đang có»): gói Free = SiteConnect Base cho tối đa 3 người — KHÔNG phải
      // «đủ mọi tính năng» (AI thuộc add-on Site Co-work). Cùng msgid với dòng dưới form đăng ký (SignupPage).
      belowCard={t`Miễn phí vĩnh viễn cho tối đa 3 người dùng với SiteConnect Base`}
      errorMap={SAAS_ERROR_MAP}
      redirectTo={redirectTo}
    />
  );
}
